import { Link, useParams } from 'react-router-dom';
import { PIP_AND_THE_PAPER_MOON, PLANNED_COLLECTIONS, STORIES, getStory } from '@/content/stories';
import { Scene } from '@/content/scenes';
import { TemplateThumb } from './TrySample';
import { Button } from '@/design-system/Button';
import { Card, Chip, EmptyState, SectionHeading } from '@/design-system/Surfaces';
import { SparkleGlyph } from '@/design-system/Sparkle';
import { CAPABILITIES } from '@/contracts/brand';
import { IconArrow } from '@/features/studio/Icons';

function Page({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="u-shell py-3xl">
      <SectionHeading eyebrow={eyebrow} title={title} description={lede} />
      <div className="mt-2xl space-y-xl">{children}</div>
    </div>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-2xl space-y-l text-[17px] leading-relaxed text-ink-soft [&_h3]:font-display [&_h3]:text-[20px] [&_h3]:font-semibold [&_h3]:text-ink [&_li]:ml-l [&_li]:list-disc [&_strong]:text-ink">
      {children}
    </div>
  );
}

/* --------------------------- W02 How it works --------------------------- */

export function HowItWorks() {
  const story = PIP_AND_THE_PAPER_MOON;
  const steps = [
    {
      title: 'They choose a story',
      body: 'Covers, an approximate length, and whether it can be read offline. Paid stories can be browsed but lead to a grown-up, never to a price tag in front of a child.',
      sceneId: 'pip-cover',
    },
    {
      title: 'They read it their way',
      body: 'Text stays real, selectable text at a size you can change. Controls stay in the same place on every page. There is no timer.',
      sceneId: 'pip-p1-goodnight',
    },
    {
      title: 'They explore it',
      body: 'A picture question and a put-it-in-order game. A wrong answer gets a hint and another go. “Help me” finishes it with them. Nothing is scored.',
      sceneId: 'pip-p4-searching',
    },
    {
      title: 'They make a picture',
      body: 'Three ready pictures from the story. Fill a shape, brush freehand, undo, done. Their work saves to the device as they go.',
      sceneId: 'pip-p5-idea',
    },
    {
      title: 'It comes to life',
      body: 'Their finished drawing moves inside the scene it came from — the same brush marks, the same colours, no re-drawing by a machine.',
      sceneId: 'pip-p6-paper-moon',
    },
  ];

  return (
    <Page
      eyebrow="How it works"
      title="From a story to something they made"
      lede="Every screenshot below is the real product, using the free story."
    >
      <ol className="space-y-xl">
        {steps.map((s, i) => (
          <li key={s.title} className="grid items-center gap-xl lg:grid-cols-2">
            <div className={i % 2 ? 'lg:order-2' : ''}>
              <Chip tone="magic">Step {i + 1}</Chip>
              <h3 className="mt-m font-display text-[24px] font-semibold text-ink">{s.title}</h3>
              <p className="mt-s max-w-lg text-[17px] leading-relaxed text-ink-soft">{s.body}</p>
            </div>
            <div className="overflow-hidden rounded-blob border-2 border-line shadow-cut">
              <Scene id={s.sceneId} className="aspect-[3/2] w-full" title={s.title} />
            </div>
          </li>
        ))}
      </ol>
      <Card className="bg-primary-soft/50 text-center">
        <h3 className="font-display text-[22px] font-semibold text-ink">Try the whole thing</h3>
        <p className="mx-auto mt-s max-w-lg text-[16px] text-ink-soft">
          {story.title} — about five minutes to read, then as long as they want to draw.
        </p>
        <Button variant="magic" size="lg" to="/try" className="mt-l" iconRight={<IconArrow />}>
          Try a story
        </Button>
      </Card>
    </Page>
  );
}

/* ----------------------------- W03 Stories ----------------------------- */

export function StoriesIndex() {
  return (
    <Page
      eyebrow="Stories"
      title="What is written, and what is coming"
      lede="One complete story is published. The rest are collections we are writing — shown as plans, not as a catalogue."
    >
      <div className="grid gap-l sm:grid-cols-2 lg:grid-cols-3">
        {STORIES.map((s) => (
          <Link
            key={s.id}
            to={`/stories/${s.slug}`}
            className="overflow-hidden rounded-blob border-2 border-line bg-card shadow-cut transition hover:-translate-y-1"
          >
            <Scene id={s.coverSceneId} className="aspect-[4/3] w-full" title={s.title} />
            <div className="p-l">
              <Chip tone="leaf">Free</Chip>
              <h3 className="mt-s font-display text-[19px] font-semibold text-ink">{s.title}</h3>
              <p className="mt-xs text-[15px] leading-relaxed text-ink-soft">{s.synopsis}</p>
            </div>
          </Link>
        ))}
      </div>

      <div>
        <h3 className="mb-l font-display text-[22px] font-semibold text-ink">Collections in progress</h3>
        <div className="grid gap-l sm:grid-cols-3">
          {PLANNED_COLLECTIONS.filter((c) => c.status === 'planned').map((c) => (
            <Card key={c.id}>
              <h4 className="font-display text-[18px] font-semibold text-ink">{c.title}</h4>
              <p className="mt-xs text-[15px] text-ink-soft">{c.description}</p>
              <Chip tone="neutral" className="mt-m">
                No stories published yet
              </Chip>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  );
}

export function StoryPreview() {
  const { slug } = useParams();
  const story = getStory(slug ?? '');
  if (!story) {
    return (
      <div className="u-shell py-3xl">
        <EmptyState
          title="That story is not here"
          body="It may have been renamed, or it may not be published yet."
          action={<Button variant="primary" to="/stories">See the stories</Button>}
        />
      </div>
    );
  }

  return (
    <div className="u-shell py-3xl">
      <div className="grid gap-2xl lg:grid-cols-[1.1fr_1fr]">
        <div className="overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
          <Scene id={story.coverSceneId} className="aspect-[3/2] w-full" title={story.title} />
        </div>
        <div>
          <Chip tone="leaf">Free · {story.collection}</Chip>
          <h1 className="mt-m font-display text-[34px] font-semibold leading-tight text-ink sm:text-[42px]">
            {story.title}
          </h1>
          <p className="mt-m text-[18px] leading-relaxed text-ink-soft">{story.synopsis}</p>

          <div className="mt-l flex flex-wrap gap-s">
            {story.themes.map((t) => (
              <Chip key={t} tone="accent">{t}</Chip>
            ))}
          </div>

          <dl className="mt-xl grid gap-m sm:grid-cols-2">
            <div>
              <dt className="font-display text-[13px] font-semibold uppercase tracking-wide text-ink-faint">Read</dt>
              <dd className="text-[16px] text-ink">About {story.currentVersion.readMinutes} minutes</dd>
            </div>
            <div>
              <dt className="font-display text-[13px] font-semibold uppercase tracking-wide text-ink-faint">Listen</dt>
              <dd className="text-[16px] text-ink">About {story.currentVersion.listenMinutes} minutes</dd>
            </div>
            <div>
              <dt className="font-display text-[13px] font-semibold uppercase tracking-wide text-ink-faint">Pictures to make</dt>
              <dd className="text-[16px] text-ink">{story.currentVersion.templates.length}</dd>
            </div>
            <div>
              <dt className="font-display text-[13px] font-semibold uppercase tracking-wide text-ink-faint">Support modes</dt>
              <dd className="text-[16px] text-ink">All three</dd>
            </div>
          </dl>

          <Button variant="magic" size="lg" to="/try" className="mt-xl" iconRight={<IconArrow />}>
            Read it now — free
          </Button>
        </div>
      </div>

      {/* An excerpt only. Paid story files are never exposed through markup
          or an unrestricted endpoint (spec §5 W02–W05). */}
      <section className="mt-3xl">
        <SectionHeading eyebrow="A taste" title="The first page" />
        <blockquote className="mt-l max-w-2xl rounded-blob border-2 border-line bg-card p-xl font-story text-[24px] leading-relaxed text-ink shadow-cut">
          “{story.currentVersion.pages[0].text}”
        </blockquote>
      </section>

      <section className="mt-3xl">
        <SectionHeading eyebrow="Pictures in this story" title="What they can make" />
        <div className="mt-l grid gap-l sm:grid-cols-3">
          {story.currentVersion.templates.map((t) => (
            <Card key={t.id}>
              <div className="aspect-square w-full rounded-pebble bg-white p-m">
                <TemplateThumb templateId={t.id} />
              </div>
              <h3 className="mt-m font-display text-[17px] font-semibold text-ink">{t.title}</h3>
              {t.livingSceneId ? (
                <Chip tone="magic" className="mt-s" icon={<SparkleGlyph className="h-3 w-3" />}>
                  Comes to life
                </Chip>
              ) : (
                <Chip tone="neutral" className="mt-s">Colour it in</Chip>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-3xl max-w-2xl">
        <SectionHeading eyebrow="For the grown-up" title="What is in the words" />
        <dl className="mt-l space-y-m">
          {Object.entries({
            Vocabulary: story.contentNotes.vocabulary,
            'Sentence length': story.contentNotes.sentenceLength,
            'Support needs': story.contentNotes.supportNeeds,
            'Talk about it': story.conversationPrompt,
          }).map(([k, v]) => (
            <div key={k} className="rounded-pebble border-2 border-line bg-card p-l">
              <dt className="font-display text-[15px] font-semibold text-ink">{k}</dt>
              <dd className="mt-xs text-[16px] text-ink-soft">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-l text-[15px] text-ink-faint">
          These are editorial notes, not a reading level or an assessment.
        </p>
      </section>
    </div>
  );
}

/* ----------------------------- W04 Pricing ----------------------------- */

export function Pricing() {
  return (
    <Page
      eyebrow="Pricing"
      title="What is free, and what is planned"
      lede="No prices are shown because no products are configured. When they are, this page will show the real terms."
    >
      <div className="grid gap-l lg:grid-cols-2">
        <Card className="bg-primary-soft/50">
          <Chip tone="leaf">Available now · £0</Chip>
          <h3 className="mt-m font-display text-[26px] font-semibold text-ink">Starter</h3>
          <ul className="mt-l space-y-s text-[16px] text-ink-soft">
            {[
              'The complete flagship story',
              'Both story activities plus the reflection',
              'All three pictures to colour',
              'The full drawing studio, with undo and autosave',
              'One living scene',
              'Everything stored on your device',
            ].map((x) => (
              <li key={x} className="flex gap-s">
                <SparkleGlyph className="mt-1 h-4 w-4 shrink-0 text-leaf" />
                {x}
              </li>
            ))}
          </ul>
          <Button variant="magic" size="lg" to="/try" className="mt-xl">
            Start now
          </Button>
        </Card>

        <Card>
          <Chip tone="neutral">Planned</Chip>
          <h3 className="mt-m font-display text-[26px] font-semibold text-ink">Family membership</h3>
          <p className="mt-m text-[16px] leading-relaxed text-ink-soft">
            Additional reviewed collections, more living scenes, and cloud sync across devices.
            One parent account covers the children on it.
          </p>
          <Prose>
            <h3 className="mt-l">Before we ask for money, this page will state</h3>
            <ul>
              <li>the actual price and billing period</li>
              <li>when it renews and how to cancel</li>
              <li>exactly which stories are included on the day you buy</li>
              <li>what stays available after it ends</li>
            </ul>
          </Prose>
          <p className="mt-l text-[15px] text-ink-faint">
            Buying on the website and buying on iPhone will grant the same rights to the same
            parent account. Purchases made in the app go through Apple.
          </p>
        </Card>
      </div>

      <Card>
        <h3 className="font-display text-[20px] font-semibold text-ink">What you keep</h3>
        <p className="mt-s max-w-2xl text-[16px] leading-relaxed text-ink-soft">
          Pictures a child finished, and keepsake editions they completed, remain viewable and
          downloadable after a membership ends, under the retention terms we publish. That is a
          different thing from unlimited future access to premium stories, and we will not blur
          the two.
        </p>
      </Card>
    </Page>
  );
}

/* --------------------------- W05 For parents --------------------------- */

export function ForParents() {
  return (
    <Page
      eyebrow="For parents"
      title="How to use it, and what it will not do"
      lede="Written for the adult, not for a marketing page."
    >
      <Prose>
        <h3>Choosing a reading mode</h3>
        <p>
          Start with <strong>Listen together</strong> for most 3s and 4s. Move to{' '}
          <strong>Read with help</strong> when a child starts finishing words ahead of the
          narration. <strong>Read myself</strong> turns narration off but keeps a tap-to-hear
          button, because reading alone should not mean reading unsupported. You can change this
          mid-story and nothing restarts.
        </p>

        <h3>What the activities are for</h3>
        <p>
          Two short games per story: one comprehension question with a right answer, one
          put-it-in-order task, plus an open reflection with no correct response. They exist to
          make the child look back at the story, not to grade them. Getting one wrong shows a hint
          and offers to reopen the relevant page. Nothing is locked behind getting it right.
        </p>

        <h3>Talking about it afterwards</h3>
        <p>
          Every story carries one conversation prompt tied to what actually happens in it. For the
          free story: <em>“Pip made a new moon instead of giving up. What would you have made?”</em>
        </p>

        <h3>Privacy choices you control</h3>
        <ul>
          <li>A child profile holds a nickname, a preset avatar and a broad age band — nothing more.</li>
          <li>Public sharing is off until you turn it on, and then applies picture by picture.</li>
          <li>You can withdraw a published picture later; the child keeps their original.</li>
          <li>The free sample uploads nothing at all.</li>
        </ul>

        <h3>What this is not</h3>
        <p>
          It is not a reading programme. Teaching a child to read involves systematic sound–letter
          work that this product does not attempt. What it offers is enjoying a story, practising
          comprehension, and making something afterwards. If you see a children's app claiming
          measured reading gains, ask what was measured and by whom.
        </p>
      </Prose>
    </Page>
  );
}

/* ----------------------------- W06 Gallery ----------------------------- */

export function GalleryPublic() {
  return (
    <Page eyebrow="Magic Gallery" title="Not open yet — and that is deliberate">
      {CAPABILITIES.publicGallery ? null : (
        <EmptyState
          title="No drawings are published"
          body="The gallery opens when a parent can approve each picture, a reviewer can check it, and there is a named person responsible for responding to reports. Until all three exist, publishing children's artwork would be the wrong thing to do."
          action={<Button variant="primary" to="/community-guidelines">Read the rules we will apply</Button>}
        />
      )}
      <Prose>
        <h3>What it will show</h3>
        <ul>
          <li>The drawing, and a chosen nickname as plain text — never a link.</li>
          <li>The story it came from, so a picture leads back to a book.</li>
          <li>A “sparkle” button. One per child, per picture, and undoable.</li>
        </ul>
        <h3>What it will never show</h3>
        <ul>
          <li>A profile page, an artist's other work, or anything clickable about the child.</li>
          <li>Comments, messages, or the ability to follow anyone.</li>
          <li>Public sparkle totals, a “most loved” list, or any ranking.</li>
        </ul>
        <p>
          Every card will carry Report picture and Hide this artist. Hiding is resolved on the
          server from the public picture ID, so the person hiding never learns who the artist is.
        </p>
      </Prose>
    </Page>
  );
}

/* --------------------------- W07 Policy pages --------------------------- */

export function Privacy() {
  return (
    <Page
      eyebrow="Privacy"
      title="What we collect, and what we do not"
      lede="This is an honest description of the software as it stands today. It is not yet a completed legal notice, and it says so."
    >
      <Card className="bg-sunny/15">
        <p className="text-[16px] leading-relaxed text-ink">
          <strong>Current status.</strong> The product runs entirely on your device. There is no
          account server, so nothing is transmitted to us, because there is nowhere for it to go.
          Before any child data is collected, this page will be replaced with a reviewed notice
          covering consent, access, deletion, retention and every vendor involved.
        </p>
      </Card>
      <Prose>
        <h3>What is stored on your device today</h3>
        <ul>
          <li>Child nickname, preset avatar, broad age band and reading preference.</li>
          <li>Which page of a story was reached, and whether an activity was completed.</li>
          <li>Drawings — the colours chosen and the brush layer — and finished pictures.</li>
        </ul>
        <h3>What is never collected</h3>
        <ul>
          <li>Full names, dates of birth, photographs, school, or location.</li>
          <li>Voice recordings. The reflection question is spoken aloud in the room and never recorded.</li>
          <li>Anything for advertising, and there are no third-party tracking SDKs.</li>
        </ul>
        <h3>Clearing everything</h3>
        <p>
          Parent settings has a control that removes every record this app has written on this
          device. If you have not signed in to sync, that is the only copy and the deletion is
          complete; if you have, the copies on the service and on your other devices are separate
          and are not removed by it. The screen tells you which of those you are in.
        </p>
      </Prose>
    </Page>
  );
}

export function Terms() {
  return (
    <Page eyebrow="Terms" title="Terms of use" lede="A plain summary while the full terms are prepared.">
      <Prose>
        <p>
          The software is provided as an early build. Stories, illustrations, characters and line
          art are original works created for this product and remain the property of its publisher.
          Drawings a child makes belong to the family that made them.
        </p>
        <p>
          There is nothing to purchase yet. When there is, cancellation, renewal and refund terms
          will appear here and on the pricing page before any payment is taken.
        </p>
        <p className="text-ink-faint">
          Publisher and jurisdiction details are outstanding owner configuration and will be filled
          in before launch.
        </p>
      </Prose>
    </Page>
  );
}

export function CommunityGuidelines() {
  return (
    <Page
      eyebrow="Community guidelines"
      title="The rules for the Magic Gallery"
      lede="Written now, so they exist before the gallery does."
    >
      <Prose>
        <h3>What can be published</h3>
        <ul>
          <li>A drawing made in this app, on one of our templates or as free drawing.</li>
          <li>A nickname that is not a real full name and identifies nobody.</li>
          <li>Nothing with readable personal details written into the picture.</li>
        </ul>
        <h3>How a picture gets there</h3>
        <ol className="ml-l list-decimal space-y-xs">
          <li>The child asks a grown-up to share it.</li>
          <li>The parent sees the exact picture and exact nickname, and approves or declines.</li>
          <li>A reviewer checks it. Both checks are re-verified at the moment of publishing.</li>
        </ol>
        <h3>Changing your mind</h3>
        <p>
          A parent can withdraw a published picture at any time. The listing and the hosted public
          copy stop being served. Copies someone already downloaded cannot be recalled — that is
          true of anything published anywhere, and we would rather say it plainly.
        </p>
        <h3>Reporting</h3>
        <p>
          Every card carries Report picture and Hide this artist. A child can report without
          fetching an adult first. Reports can suspend a picture immediately while it is looked at,
          without ever touching the family's private original.
        </p>
      </Prose>
    </Page>
  );
}

export function Accessibility() {
  return (
    <Page
      eyebrow="Accessibility"
      title="What is built, and what is untested"
      lede="Split honestly, because an accessibility page full of unverified claims is worse than none."
    >
      <div className="grid gap-l lg:grid-cols-2">
        <Card className="bg-leaf/10">
          <h3 className="font-display text-[19px] font-semibold text-ink">Built and working</h3>
          <ul className="mt-m space-y-s text-[16px] text-ink-soft">
            {[
              'Every control is at least 48px, most child-facing ones 56px',
              'Adjustable story text size, from 20px to 44px',
              'Reduced motion is honoured, including the living scene',
              'Status is never signalled by colour alone — there is always a word',
              'Sequence tasks have a tap-to-place alternative to dragging',
              'Questions can be read aloud on demand',
              'Keyboard undo/redo and tool shortcuts in the studio',
              'Visible focus outlines throughout',
            ].map((x) => (
              <li key={x} className="flex gap-s">
                <SparkleGlyph className="mt-1 h-4 w-4 shrink-0 text-leaf" />
                {x}
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <h3 className="font-display text-[19px] font-semibold text-ink">Not verified yet</h3>
          <ul className="mt-m space-y-s text-[16px] text-ink-soft">
            {[
              'Full screen-reader testing on VoiceOver with a real user',
              'Apple Pencil pressure and palm rejection on hardware',
              'Recorded narration — the app currently uses device speech',
              'Word-level highlighting, which needs accurate timings we do not have',
              'Contrast audit across every state at every text size',
            ].map((x) => (
              <li key={x} className="flex gap-s">
                <span aria-hidden className="mt-[6px] h-2 w-2 shrink-0 rounded-full bg-ink-faint" />
                {x}
              </li>
            ))}
          </ul>
          <p className="mt-l text-[15px] text-ink-faint">
            We do not describe the app as dyslexia-friendly, because that has not been tested.
          </p>
        </Card>
      </div>
    </Page>
  );
}

export function Help() {
  return (
    <Page eyebrow="Help" title="Common problems">
      <Prose>
        <h3>“My picture did not save”</h3>
        <p>
          The studio only says <strong>Saved on this device</strong> after the write actually
          succeeds. If it says it could not save, your drawing is still on screen — press Done
          again. The usual cause is a full device or a private browsing window.
        </p>
        <h3>“It says saved on this device, not saved everywhere”</h3>
        <p>
          That is accurate. Cloud sync is not built yet, so your work exists on this device only.
          Clearing your browser data would remove it.
        </p>
        <h3>“The gallery is empty”</h3>
        <p>It is not open. See <Link className="text-primary underline" to="/gallery">the gallery page</Link>.</p>
        <h3>Still stuck</h3>
        <p className="text-ink-faint">
          A support contact address is outstanding owner configuration. It will appear here and in
          the footer before launch, rather than a form that goes nowhere.
        </p>
      </Prose>
    </Page>
  );
}

export function Contact() {
  return (
    <Page eyebrow="Contact" title="Getting in touch">
      <Card className="bg-sunny/15">
        <p className="text-[16px] leading-relaxed text-ink">
          There is no contact channel configured yet, so this page does not show a form that would
          silently discard what you wrote. A real address and a response target are on the list of
          things needed before launch.
        </p>
      </Card>
    </Page>
  );
}

export function NotFound() {
  return (
    <div className="u-shell py-3xl">
      <EmptyState
        title="This page is not here"
        body="The link may be old, or the page may not be built yet."
        action={<Button variant="primary" to="/">Back to the start</Button>}
      />
    </div>
  );
}
